// Aggregate the /assets/:id/pois API response: the endpoint returns one ROW PER LOCATION
// for POIs assigned to multiple spaces (the server UNION join), so consumers must merge
// duplicate ids into one POI carrying a locations[] array.
//
// D-8 (2026-07-06): moved verbatim from AutodeskViewer.loadPOIsForAsset and adopted by the
// IFC POI layer, which previously fed raw rows straight into its POI service — duplicate
// ids overwrote each other there, so a multi-space POI showed a NONDETERMINISTIC single
// location (whichever row happened to come last).

import { isStoreyType } from '../services/spatial/StoreyClassifier';

export interface AggregatedPOILocation {
  id: any;
  name: string;
  type: string;
  level_name?: string;
  ifc_id?: any;
  source_file?: string;
  elevation?: any;
  height?: any;
}

// Room-assignment coverage over AGGREGATED pois (locations[] present). Feeds the
// "N POIs are not assigned to rooms" indicator: a fresh RVT site imports every POI at
// storey level (rooms don't exist yet / import never matches them), which silently breaks
// every room-scoped feature until "Reassign locations" is run — this makes it visible.
// storeyOnly deliberately means "no Spaces assignment" regardless of how the storey was
// assigned (direct location_id or junction rows), per the aggregation contract above.
export interface RoomAssignmentStats {
  total: number;
  spaceAssigned: number; // ≥1 real Spaces assignment
  storeyOnly: number;    // no Spaces assignment; direct location is a storey
  unassigned: number;    // no Spaces assignment and no storey either
}

export const computeRoomAssignmentStats = (pois: any[]): RoomAssignmentStats => {
  const stats: RoomAssignmentStats = { total: 0, spaceAssigned: 0, storeyOnly: 0, unassigned: 0 };
  (pois || []).forEach((poi: any) => {
    stats.total += 1;
    if (Array.isArray(poi?.locations) && poi.locations.length > 0) stats.spaceAssigned += 1;
    else if (isStoreyType(poi?.location_type)) stats.storeyOnly += 1;
    else stats.unassigned += 1;
  });
  return stats;
};

// DB-truth room assignments for a POI — the names of the Spaces rows aggregated below.
// This is the ONLY valid seed for room-assignment editing: display fields may fall back
// to metadata-derived room names (custom_data), but seeding edits from the display string
// creates phantom assignments — the save round-trips names the server has no Space row
// for, silently persists nothing, and the metadata keeps the phantom visible.
export const assignedRoomNames = (poi: any): string[] =>
  Array.isArray(poi?.locations)
    ? poi.locations.map((l: AggregatedPOILocation) => l?.name).filter(Boolean)
    : [];

export const aggregatePOIRows = (rows: any[]): any[] => {
  const poiMap = new Map<number, any>();

  (rows || []).forEach((poi: any) => {
    const poiId = poi.id;
    if (poiMap.has(poiId)) {
      // Add location to existing POI's locations array
      const existingPoi = poiMap.get(poiId);
      if (poi.location_name && poi.location_type === 'Spaces') {
        const locationExists = existingPoi.locations.some(
          (l: AggregatedPOILocation) => l.name === poi.location_name
        );
        if (!locationExists) {
          existingPoi.locations.push({
            id: poi.effective_location_id,
            name: poi.location_name,
            type: poi.location_type,
            level_name: poi.parent_location_name,
            ifc_id: poi.location_ifc_id,
            source_file: poi.location_source_file,
            elevation: poi.location_elevation,
            height: poi.location_height
          });
        }
      }
    } else {
      // Create new POI entry with locations array
      const locations: AggregatedPOILocation[] = [];
      if (poi.location_name && poi.location_type === 'Spaces') {
        locations.push({
          id: poi.effective_location_id,
          name: poi.location_name,
          type: poi.location_type,
          level_name: poi.parent_location_name,
          ifc_id: poi.location_ifc_id,
          source_file: poi.location_source_file,
          elevation: poi.location_elevation,
          height: poi.location_height
        });
      }
      poiMap.set(poiId, { ...poi, locations });
    }
  });

  return Array.from(poiMap.values());
};
